import { ZoneHelpers } from '@diagram/helpers/ZoneHelpers';

import { CreditUsageType, CreditUsageTypeCosts, POWER_FLOW_BATCH_TIERS, PowerFlowDiagram } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';

import { workspaceConfigState } from 'state/workspace-config';

const CreditHelpers = {
    swr: {
        creditBalanceDetails: (teamId: string) => `/teams/${teamId}/credit-balance-details`,
    },

    formatters: {
        credits: (typeOrAmount: CreditUsageType | number, amount = 1) => {
            const format = (credits: number) => {
                return credits === 1 ? '1 credit' : `${IntlHelpers.formatNumber(credits)} credits`;
            };

            if (typeof typeOrAmount === 'number') {
                return format(typeOrAmount);
            }

            const costPerUnit = CreditUsageTypeCosts[typeOrAmount];

            return format(costPerUnit * amount);
        },
    },

    // The quote before a run and the charge after it both price through here, so they cannot drift apart.
    powerFlow: {
        // `rows` is null for a snapshot. Every island multiplies the whole price, so an AC and a DC
        // architecture drawn side by side cost twice as much as either one alone.
        credits: ({ diagram, rows }: { diagram: PowerFlowDiagram; rows: number | null }) => {
            const islands = CreditHelpers.powerFlow.islands(diagram);

            if (rows === null) {
                return CreditUsageTypeCosts[CreditUsageType.POWER_FLOW_SIMULATION] * islands;
            }

            const credits = POWER_FLOW_BATCH_TIERS.reduce((total, { min, max, creditsPerRow }) => {
                const rowsInTier = Math.max(0, Math.min(rows, max) - min + 1);

                return total + rowsInTier * creditsPerRow;
            }, 0);

            return Math.ceil(credits) * islands;
        },

        // A run always solves at least one island, even on a diagram with nothing connected yet.
        islands: (diagram: PowerFlowDiagram) => Math.max(1, ZoneHelpers.islands(diagram).length),

        csvRows: (csv: string) => {
            const lines = csv
                .trim()
                .split('\n')
                .filter((line) => line.trim() !== '');

            // The first line is the header
            return Math.max(0, lines.length - 1);
        },
    },

    hidden: (team?: { hideCredits?: boolean } | null) =>
        team?.hideCredits === true || workspaceConfigState.config.hideCredits === true,

    sufficient: (balance = 0, type: CreditUsageType, amount = 1) => {
        const costPerUnit = CreditUsageTypeCosts[type];

        return costPerUnit * amount <= balance;
    },

    insufficient: (balance = 0, type: CreditUsageType, amount = 1) => {
        const costPerUnit = CreditUsageTypeCosts[type];

        return costPerUnit * amount > balance;
    },
};

export { CreditHelpers };
